import { Suspense } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import axios from 'axios'

import RecipeView from '@/recipe/components/RecipeView'
import { Dialog, Input, Button, DialogPanel, Select, Field } from '@headlessui/react'
import Loader from '@/components/Loader'

import { useExtractRecipeMutation, useMyRecipesQuery } from '@/recipe/api'
import { ChevronDownIcon } from '@heroicons/react/16/solid'

import { LANGUAGES, COMMON_LANGUAGES, formatLanguageOption } from '@/helpers/languages'
import { useMeQuery, usePatchUserSettingsMutation } from '@/auth/queries/auth'

const schema = z.object({
  url: z.string().url({ message: 'Please enter a valid video URL' }),
  language: z.enum(LANGUAGES as [string, ...string[]]),
})

type FormValues = z.infer<typeof schema>

const HomePage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: me } = useMeQuery()
  const { mutateAsync: patchUser } = usePatchUserSettingsMutation()

  const { register, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      language: me?.settings?.recipeLanguage || (navigator.language.split('-')[0] as FormValues['language']),
    },
  })

  const { data: recipes } = useMyRecipesQuery()
  const extractRecipe = useExtractRecipeMutation()

  const onSubmit = ({ url, language }: FormValues) => {
    extractRecipe.mutate(
      { url, language },
      {
        onSuccess: (res) => {
          reset()
          navigate(`/recipes/${res.id}`)
        },
        onError: (error) => {
          if (axios.isAxiosError(error) && error.response?.data?.message) {
            toast.error(error.response.data.message)
          } else {
            toast.error('Something went wrong. Please try again later.')
          }
        },
      },
    )
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col overflow-hidden p-4">
      <h1 className="mb-4 text-center text-2xl font-medium">Your AI Recipe Assistant</h1>
      <div className="flex h-full flex-col gap-3 divide-y divide-white/5 overflow-auto rounded-lg bg-white/5 p-0 shadow-md">
        <form onSubmit={handleSubmit(onSubmit)} className="flex px-5 py-3">
          <Input
            {...register('url')}
            className={clsx(
              'block w-full rounded-l-lg border-none bg-white/5 px-3 py-1.5 text-sm/6 text-white',
              'data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25',
            )}
            placeholder="Instagram video link"
          />
          <Field className="relative w-64">
            <Select
              {...register('language', {
                onChange: (e) => {
                  patchUser({ recipeLanguage: e.target.value })
                },
              })}
              className={clsx(
                'mr-3 block w-full appearance-none border-none bg-white/5 px-3 py-1.5 text-sm/6 text-white',
                'focus:not-data-focus:outline-none data-focus:outline-2 data-focus:-outline-offset-2 data-focus:outline-white/25',
                // Make the text of each option black on Windows
                '*:text-black',
              )}
            >
              {COMMON_LANGUAGES.map((language) => (
                <option key={`common-${language}`} value={language}>
                  {formatLanguageOption(language)}
                </option>
              ))}
            </Select>
            <ChevronDownIcon
              className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
              aria-hidden="true"
            />
          </Field>
          <Button
            type="submit"
            className="inline-flex items-center gap-2 rounded-r-md bg-neutral-700 px-3 py-1.5 text-sm/6 font-semibold text-white shadow-inner shadow-white/10 focus:outline-none data-[focus]:outline-1 data-[focus]:outline-white data-[hover]:bg-neutral-600 data-[open]:bg-neutral-700"
            disabled={extractRecipe.isPending}
          >
            Scan
          </Button>
        </form>
        {recipes?.length === 0 && <p className="px-5 pt-0 pb-3 text-gray-300">You haven't scanned any recipes yet.</p>}
        {!!recipes?.length && (
          <ul className="space-y-3 overflow-y-auto px-5 pb-5">
            {recipes.map((r) => (
              <li
                key={r.id}
                className="cursor-pointer rounded-md px-4 py-3 text-sm/6 transition hover:bg-white/5"
                onClick={() => navigate('/recipes/' + r.id)}
              >
                <h2 className="text-lg font-medium">{r.title}</h2>
                <p className="text-sm text-gray-500">Created at {new Date(r.createdAt).toLocaleString()}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Dialog
        open={!!id || extractRecipe.isPending}
        as="div"
        className="relative z-10 text-white focus:outline-none"
        onClose={() => navigate('/')}
      >
        <div className="fixed inset-0 z-10 w-screen">
          <div className="flex h-screen items-center justify-center p-4">
            <DialogPanel
              transition
              className="h-[85%] w-full max-w-3xl overflow-y-auto rounded-xl bg-white/5 p-3 backdrop-blur-2xl duration-300 ease-out data-[closed]:transform-[scale(95%)] data-[closed]:opacity-0"
            >
              {extractRecipe.isPending && (
                <div className="flex h-full flex-col items-center justify-center gap-2">
                  <Loader />
                  <div className="flex flex-col items-center gap-1">
                    <p className="text-sm text-gray-300">Cooking...</p>
                    <p className="text-sm text-gray-300">This may take up to 1 minute</p>
                  </div>
                </div>
              )}
              {id && (
                <Suspense
                  fallback={
                    <div className="flex h-full items-center justify-center">
                      <Loader />
                    </div>
                  }
                >
                  <RecipeView id={id} />
                </Suspense>
              )}
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

export default HomePage
